/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {dlsLayout} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import Slider from '../slider/Slider';

export interface ProgressControlProps {
  progress: number;
  duration: number;
  handleSeekChange: Function;
}

// the slider spans its container - the current theme's slider is otherwise
// a fixed width, the legacy one spans it already ...
const FULL_WIDTH = {width: '100%'};

const useStyles = makeStyles((theme) => ({
  progress : {
    marginTop    : -dlsLayout('spacing'),
    marginBottom : dlsLayout('spacing') - 2,
    width        : '100%'
  },
  // inset by half the current theme's slider thumb, so the thumb stays in
  // view at either end of the player ...
  currentProgress : {
    boxSizing : 'border-box',
    padding   : '0 8px'
  }
}));

/**
 * @ProgressControl
 *
 * Defines the Progress Bar Component. Once the video started playing the progress bar
 * will be loaded based on played seconds of react Video player
 *
 * Figma link to refer Progress bar component:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/02_DLS_Components?node-id=4060%3A43298
 *
 * ```typescript
 * import ProgressControl from '@ion/dls/src/components/videoplayer/ProgressControl';
 *
 * function sliderChange(playedSeconds) {
 *   setProgress(playedSeconds);
 * }

 * //API called when video is in progress
 * function handleProgress(state) {
 *   sliderChange(state.playedSeconds);
 * };
 *
 * <ProgressControl progress={progress}
 *     duration={duration}
 *     sliderChange={sliderChange}
 *     fullScreen={fullScreen} />
 *
 * ```
*/
const ProgressControl = ({
  progress,
  duration,
  handleSeekChange
}: ProgressControlProps) => {
  const classes     = useStyles();
  const {isCurrent} = useThemeContext();

  return (
    <div className={isCurrent ? `${classes.progress} ${classes.currentProgress}` : classes.progress}>
      <Slider style={FULL_WIDTH}
        min={0}
        max={duration}
        value={progress}
        onSlide={function(e, val) { handleSeekChange(val); }} />
    </div>
  );
};

export default ProgressControl;
